import type { ShubhMuhuratResponse } from "@/lib/api";

/**
 * Server-rendered display for the curated shubh-muhurat digest. Shared by the
 * en + hi city pages so the two locales render the same component tree (the
 * monetization/value moment can never be left behind on one locale — rule 46).
 * All values come from the engine digest (rule 29); times are tz-aware (rule 25).
 */

type Locale = "en" | "hi";

export function todayISTDateString(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

/** One-line "best window today" verdict — the page's distinct value moment. */
export function BestWindowVerdict({
  digest,
  cityName,
  locale = "en",
}: {
  digest: ShubhMuhuratResponse | null;
  cityName: string;
  locale?: Locale;
}) {
  // Optional-chain the array itself: the digest is server-primed from the live
  // API and soft-fails to null (below), but a partial/older response (e.g. a
  // frontend/backend deploy skew where `recommended_windows` is not yet served)
  // must degrade the same way, never crash the prerender on `undefined[0]`.
  const best = digest?.recommended_windows?.[0];
  if (!best) return null;
  const hi = locale === "hi";
  return (
    <div
      className="card-sacred p-4 mb-6"
      style={{ borderColor: "var(--ish-accent-border)", borderWidth: 1 }}
    >
      <p
        className="text-[11px] uppercase tracking-wider font-semibold mb-1"
        style={{ color: "var(--ish-accent)" }}
      >
        {hi ? "आज का श्रेष्ठ मुहूर्त" : "Best window today"}
      </p>
      <p className="text-[15px] font-semibold" style={{ color: "var(--ish-dark)" }}>
        {hi
          ? `${cityName} में ${best.name_hi} ${best.start}–${best.end} का सुझाया गया सुरक्षित समय है।`
          : `For ${digest.purpose_label_en.toLowerCase()} in ${cityName}, ${best.name_en} (${best.start}–${best.end}) is the first avoid-window-safe choice.`}
      </p>
    </div>
  );
}

export function ShubhMuhuratDigest({
  digest,
  locale = "en",
}: {
  digest: ShubhMuhuratResponse | null;
  locale?: Locale;
}) {
  const hi = locale === "hi";
  if (!digest) {
    return (
      <p className="text-[13px] mb-6" style={{ color: "var(--ish-text-muted)" }}>
        {hi
          ? "मुहूर्त डेटा अभी लोड नहीं हो सका। कृपया कुछ देर बाद पुनः प्रयास करें।"
          : "Muhurat data could not load right now. Please try again shortly."}
      </p>
    );
  }

  return (
    <>
      {(digest.recommended_windows?.length ?? 0) > 0 && (
        <>
          <DigestSubhead>
            {hi
              ? `${digest.purpose_label_hi} के लिए सुझाए गए समय`
              : `Recommended for ${digest.purpose_label_en.toLowerCase()}`}
          </DigestSubhead>
          <div className="mb-6 grid gap-2 sm:grid-cols-2">
            {digest.recommended_windows.slice(0, 4).map((window, index) => (
              <div key={window.key} className="card-sacred p-3.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-ish-accent">
                  {index === 0 ? (hi ? "पहला विकल्प" : "First choice") : hi ? "विकल्प" : "Option"}
                </p>
                <p className="mt-1 text-sm font-semibold text-ish-dark">
                  {hi ? window.name_hi : window.name_en}
                </p>
                <p className="mt-1 text-sm text-ish-text-muted">
                  {window.start}–{window.end}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
      <DigestSubhead>{hi ? "आज के शुभ मुहूर्त" : "Today's Auspicious Muhurats"}</DigestSubhead>
      <div className="space-y-2 mb-6">
        {digest.named_muhurats.map((m) => (
          <div key={m.key} className="card-sacred p-3.5 flex items-start justify-between gap-3">
            <div>
              <p className="text-[13px] font-semibold" style={{ color: "var(--ish-dark)" }}>
                {hi ? m.name_hi : m.name_en}
              </p>
              <p className="text-[12px] mt-1" style={{ color: "var(--ish-text-muted)" }}>
                {hi ? m.summary_hi : m.summary_en}
              </p>
            </div>
            <span
              className="text-[13px] font-semibold whitespace-nowrap"
              style={{ color: "var(--ish-accent)" }}
            >
              {m.start}–{m.end}
            </span>
          </div>
        ))}
      </div>

      {digest.auspicious_choghadiya.length > 0 && (
        <>
          <DigestSubhead>
            {hi ? "श्रेष्ठ चौघड़िया अवधियाँ" : "Best Choghadiya Windows"}
          </DigestSubhead>
          <div className="flex flex-wrap gap-2 mb-6">
            {digest.auspicious_choghadiya.map((c, i) => (
              <span
                key={`${c.name_en}-${c.start}-${i}`}
                className="px-3 py-1.5 rounded-lg text-[12px] font-medium"
                style={{
                  backgroundColor: "var(--ish-accent-light)",
                  color: "var(--ish-accent)",
                  border: "1px solid var(--ish-accent-border)",
                }}
              >
                {hi ? c.name_hi : c.name_en} {c.start}–{c.end}
              </span>
            ))}
          </div>
        </>
      )}

      <DigestSubhead>{hi ? "इन समयों से बचें" : "Times to Avoid"}</DigestSubhead>
      <div className="space-y-2 mb-6">
        {digest.inauspicious.map((w) => (
          <div key={w.key} className="card-sacred p-3.5 flex items-center justify-between gap-3">
            <p className="text-[13px] font-semibold" style={{ color: "var(--ish-dark)" }}>
              {hi ? w.name_hi : w.name_en}
            </p>
            <span
              className="text-[13px] font-semibold whitespace-nowrap"
              style={{ color: "var(--ish-text-faint)" }}
            >
              {w.start}–{w.end}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function DigestSubhead({ children }: { children: React.ReactNode }) {
  return (
    <h2
      className="text-[17px] font-bold mt-2 mb-3"
      style={{
        fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
        color: "var(--ish-dark)",
      }}
    >
      {children}
    </h2>
  );
}
